import {z} from 'zod/v4';
import {
  agenticWorkflowIdSchema,
  objectIdStringSchema,
  workflowDraftMutationIdSchema,
  workflowVersionIdSchema,
} from './agentic-workflow-identifiers';
import {
  agentWorkflowDefinitionSchema,
  type AgentWorkflowDefinition,
} from './workflow-draft';

export const DEFAULT_WORKFLOW_VERSION_LIST_LIMIT = 20;
export const MAX_WORKFLOW_VERSION_LIST_LIMIT = 100;

const positiveIntegerQueryParameterSchema = z
  .string()
  .regex(/^[1-9]\d*$/)
  .transform(Number)
  .pipe(z.number().int().positive().safe());

const workflowVersionIdV4Schema = z.number().transform((versionId, context) => {
  const result = workflowVersionIdSchema.safeParse(versionId);
  if (!result.success) {
    context.addIssue({
      code: 'custom',
      message: 'Invalid workflow version ID',
    });
    return z.NEVER;
  }

  return result.data;
});

const agenticWorkflowIdV4Schema = z
  .string()
  .transform((workflowId, context) => {
    const result = agenticWorkflowIdSchema.safeParse(workflowId);
    if (!result.success) {
      context.addIssue({
        code: 'custom',
        message: 'Invalid agentic workflow ID',
      });
      return z.NEVER;
    }
    return result.data;
  });

const workflowDraftMutationIdV4Schema = z
  .string()
  .transform((mutationId, context) => {
    const result = workflowDraftMutationIdSchema.safeParse(mutationId);
    if (!result.success) {
      context.addIssue({
        code: 'custom',
        message: 'Invalid workflow draft mutation ID',
      });
      return z.NEVER;
    }
    return result.data;
  });

const objectIdStringV4Schema = z.string().transform((userId, context) => {
  const result = objectIdStringSchema.safeParse(userId);
  if (!result.success) {
    context.addIssue({
      code: 'custom',
      message: 'Invalid object ID',
    });
    return z.NEVER;
  }
  return result.data;
});

export const listWorkflowVersionsQuerySchema = z
  .object({
    limit: positiveIntegerQueryParameterSchema
      .optional()
      .transform((limit) => limit ?? DEFAULT_WORKFLOW_VERSION_LIST_LIMIT)
      .pipe(z.number().max(MAX_WORKFLOW_VERSION_LIST_LIMIT)),
    before: positiveIntegerQueryParameterSchema
      .pipe(workflowVersionIdV4Schema)
      .optional(),
  })
  .strict();

export type ListWorkflowVersionsQuery = z.infer<
  typeof listWorkflowVersionsQuerySchema
>;

export const workflowVersionListItemSchema = z
  .object({
    versionId: workflowVersionIdV4Schema,
    isActiveVersion: z.boolean(),
    notes: z.string().max(1000).nullable(),
    savedOn: z.string().datetime(),
    savedByUserId: z.string().regex(/^[0-9a-f]{24}$/i),
    sourceDraftRevision: z.number().int().positive(),
  })
  .strict();

export type WorkflowVersionListItem = z.infer<
  typeof workflowVersionListItemSchema
>;

export const workflowVersionDetailsSchema =
  workflowVersionListItemSchema.extend({
    definition: agentWorkflowDefinitionSchema,
  });

export type WorkflowVersionDetails = WorkflowVersionListItem & {
  definition: AgentWorkflowDefinition;
};

export const listWorkflowVersionsResponseSchema = z
  .object({
    versions: z.array(workflowVersionListItemSchema),
    publishedVersion: workflowVersionListItemSchema.nullable().default(null),
    hasMore: z.boolean(),
  })
  .strict();

export type ListWorkflowVersionsResponse = z.infer<
  typeof listWorkflowVersionsResponseSchema
>;

export const switchActiveWorkflowVersionRequestSchema = z
  .object({
    mutationId: workflowDraftMutationIdV4Schema,
    targetVersionId: workflowVersionIdV4Schema,
    expectedActiveVersionId: workflowVersionIdV4Schema,
  })
  .strict()
  .refine(
    (value) => value.targetVersionId !== value.expectedActiveVersionId,
    'The target workflow version must differ from the active workflow version'
  );

export type SwitchActiveWorkflowVersionRequest = z.infer<
  typeof switchActiveWorkflowVersionRequestSchema
>;

export const switchActiveWorkflowVersionResponseSchema = z
  .object({
    workflowId: agenticWorkflowIdV4Schema,
    activeVersionId: workflowVersionIdV4Schema,
    previousActiveVersionId: workflowVersionIdV4Schema,
    status: z.literal('active'),
    switchedOn: z.string().datetime(),
    switchedByUserId: objectIdStringV4Schema,
  })
  .strict();

export type SwitchActiveWorkflowVersionResponse = z.infer<
  typeof switchActiveWorkflowVersionResponseSchema
>;
